import { useFocusRing } from 'react-aria'
import React, { useCallback, useLayoutEffect, useRef, useState } from 'react'

export const useFocusVisible = (forceFocus?: boolean) => {
  // `react-aria` uses element.focus() in `useButton` breaking `:focus-visible` on macOS on all browsers - see https://bugzilla.mozilla.org/show_bug.cgi?id=1618639#c10
  const { focusProps, isFocusVisible: isRealFocusVisible } = useFocusRing()
  const isFocusVisible = forceFocus || isRealFocusVisible
  return { focusProps, isFocusVisible }
}
export const useElementSize = (elementRef: React.RefObject<HTMLElement | null>) => {
  const [width, setWidth] = useState(0)
  const [height, setHeight] = useState(0)

  useLayoutEffect(() => {
    const observer = new ResizeObserver(([trigger]) => {
      setWidth(trigger.target.getBoundingClientRect().width)
      setHeight(trigger.target.getBoundingClientRect().height)
    })
    if (elementRef.current) {
      observer.observe(elementRef.current)
    }
    return () => {
      observer.disconnect()
    }
  }, [elementRef])
  return { width, height }
}

export const useElementWidth = (elementRef: React.RefObject<HTMLElement | null>) => {
  const [width, setWidth] = useState<number | undefined>(undefined)
  const lastObservedWidth = useRef<number | undefined>(undefined)

  useLayoutEffect(() => {
    const observer = new ResizeObserver(([trigger]) => {
      const newWidth = trigger.target.getBoundingClientRect().width
      if (newWidth !== lastObservedWidth.current) {
        // Update only if it would change the result of the getWidth function
        setWidth(newWidth)
      }
    })
    if (elementRef.current) {
      observer.observe(elementRef.current)
    }
    return () => {
      observer.disconnect()
    }
  }, [elementRef])

  const getWidth = useCallback(() => {
    const result = width ?? elementRef.current?.getBoundingClientRect().width
    lastObservedWidth.current = result
    return result
  }, [width, elementRef])

  return { getWidth }
}
